import React, { useState, useEffect, useCallback, useRef } from 'react';
import Icon from '../../components/AppIcon';
import { buscarProducto, guardarPrecio, getHistoricoPvp, getAuditoriaPrecios, getDashboardKpis, getProductosFiltrados, aplicarCambioMasivo, getFamilias, getProveedores, calcPvpTeorico, calcMargenReal, aplicarRedondeo, calcSemaforo, SEMAFORO_COLORS,  } from '../../services/priceManagementService';

const ACCENT = '#D6EF4E';

const fmt = (n, dec = 2) => (n != null && !isNaN(n)) ? Number(n)?.toFixed(dec) + ' €' : '—';
const fmtPct = (n) => (n != null && !isNaN(n)) ? Number(n)?.toFixed(2) + ' %' : '—';

const REGLAS_REDONDEO = [
  { value: 'SIN_REDONDEO', label: 'Sin redondeo' },
  { value: 'TERMINAR_90', label: 'Terminar en ,90' },
  { value: 'TERMINAR_95', label: 'Terminar en ,95' },
  { value: 'TERMINAR_99', label: 'Terminar en ,99' },
  { value: 'MULTIPLO_05', label: 'Múltiplo de 0,05' },
  { value: 'MULTIPLO_10', label: 'Múltiplo de 0,10' },
];

// ─── SEMÁFORO BADGE ──────────────────────────────────────────────────────────
const SemaforoBadge = ({ margenReal, config }) => {
  const estado = calcSemaforo(margenReal, config?.minimo, config?.objetivo, config?.premium);
  const c = SEMAFORO_COLORS?.[estado];
  return (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold"
      style={{ background: c?.bg, border: `1px solid ${c?.border}`, color: c?.text }}>
      <span className="w-2 h-2 rounded-full" style={{ background: c?.text }} />
      {c?.label}
    </span>
  );
};

// ─── FICHA PRODUCTO ──────────────────────────────────────────────────────────
const FichaProducto = ({ producto, config, onSaved }) => {
  const [margenEdit, setMargenEdit] = useState(producto?.margenObjetivo || 35);
  const [nuevoPvp, setNuevoPvp] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState(null);
  const [historico, setHistorico] = useState([]);
  const [showHistorico, setShowHistorico] = useState(false);

  const coste = producto?.costeReal || 0;
  const margenDec = margenEdit / 100;
  const pvpTeorico = calcPvpTeorico(coste, margenDec);
  const regla = producto?.familiaReglaRedondeo || 'TERMINAR_99';
  const pvpSugerido = pvpTeorico ? aplicarRedondeo(pvpTeorico, regla) : null;
  const pvpFinal = parseFloat(nuevoPvp) || pvpSugerido || producto?.pvpActual || 0;
  const margenFinal = calcMargenReal(pvpFinal, coste);
  const semaforo = calcSemaforo(margenFinal, config?.minimo, config?.objetivo, config?.premium);
  const sc = SEMAFORO_COLORS?.[semaforo];

  useEffect(() => {
    setMargenEdit(producto?.margenObjetivo || 35);
    setNuevoPvp('');
    setSaveMsg(null);
  }, [producto?.id]);

  const handleSave = async () => {
    if (!pvpFinal || pvpFinal <= 0) return;
    setSaving(true);
    setSaveMsg(null);
    try {
      await guardarPrecio(producto?.id, pvpFinal, {
        costeReal: coste,
        margenNuevo: margenFinal,
        margenAnterior: producto?.margenReal,
        pvpAnterior: producto?.pvpActual,
        pvpSugerido,
        pvpTeorico,
        codigoEpelsa: producto?.codigo_epelsa,
        usuario: 'ERP',
        origen: 'MANUAL',
      });
      setSaveMsg({ ok: true, msg: 'Precio guardado. Sync Epelsa: PENDIENTE' });
      onSaved?.();
    } catch (e) {
      setSaveMsg({ ok: false, msg: e?.message || 'Error al guardar' });
    } finally {
      setSaving(false);
    }
  };

  const loadHistorico = async () => {
    if (showHistorico) { setShowHistorico(false); return; }
    try {
      const h = await getHistoricoPvp(producto?.id);
      setHistorico(h);
      setShowHistorico(true);
    } catch {}
  };

  const diferencia = pvpSugerido && producto?.pvpActual
    ? (pvpSugerido - producto?.pvpActual)
    : null;

  return (
    <div className="space-y-4">
      {/* Info producto */}
      <div className="rounded-2xl p-4" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}>
        <div className="flex items-start justify-between gap-3 mb-3">
          <div>
            <h2 className="font-oranienbaum font-bold text-white text-lg leading-tight">{producto?.nombre}</h2>
            <p className="font-oranienbaum text-white/40 text-sm mt-0.5">
              {producto?.codigo_interno && <span className="mr-3">Cód: {producto?.codigo_interno}</span>}
              {producto?.ean && <span className="mr-3">EAN: {producto?.ean}</span>}
              {producto?.plu && <span>PLU: {producto?.plu}</span>}
            </p>
          </div>
          <SemaforoBadge margenReal={margenFinal} config={config} />
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {[
            { label: 'Familia', value: producto?.familiaNombre, icon: 'Tag' },
            { label: 'Proveedor', value: producto?.proveedorNombre, icon: 'Truck' },
            { label: 'Stock', value: producto?.stockActual != null ? `${producto?.stockActual} ud` : '—', icon: 'Package', alert: producto?.stockActual < 0 },
            { label: 'Cód. Epelsa', value: producto?.codigo_epelsa || '—', icon: 'Scale' },
          ]?.map((item, i) => (
            <div key={i} className="rounded-xl p-2.5" style={{ background: 'rgba(255,255,255,0.03)', border: `1px solid ${item?.alert ? 'rgba(239,68,68,0.3)' : 'rgba(255,255,255,0.06)'}` }}>
              <p className="font-oranienbaum text-white/35 text-xs mb-0.5">{item?.label}</p>
              <p className={`font-oranienbaum font-semibold text-sm truncate ${item?.alert ? 'text-red-400' : 'text-white/80'}`}>{item?.value}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Motor de precios */}
      <div className="rounded-2xl p-4" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}>
        <h3 className="font-oranienbaum font-bold text-white mb-4 text-sm uppercase tracking-wider opacity-60">Motor de Precios</h3>

        {/* Fila costes */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4">
          <div className="rounded-xl p-3" style={{ background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.2)' }}>
            <p className="font-oranienbaum text-red-400/70 text-xs mb-1">Coste s/IVA</p>
            <p className="font-oranienbaum font-bold text-red-400 text-lg">{fmt(producto?.precio_compra)}</p>
          </div>
          <div className="rounded-xl p-3" style={{ background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.2)' }}>
            <p className="font-oranienbaum text-red-400/70 text-xs mb-1">IVA {producto?.iva}% + Rec {producto?.recargo}%</p>
            <p className="font-oranienbaum font-bold text-red-400 text-lg">{fmt(coste)}</p>
          </div>
          <div className="rounded-xl p-3 col-span-2 md:col-span-1" style={{ background: 'rgba(214,239,78,0.08)', border: '1px solid rgba(214,239,78,0.2)' }}>
            <p className="font-oranienbaum text-[#D6EF4E]/70 text-xs mb-1">Coste real c/impuestos</p>
            <p className="font-oranienbaum font-bold text-[#D6EF4E] text-lg">{fmt(coste)}</p>
          </div>
        </div>

        {/* Margen editable */}
        <div className="rounded-xl p-3 mb-4" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}>
          <div className="flex items-center justify-between mb-2">
            <p className="font-oranienbaum text-white/60 text-sm">Margen objetivo</p>
            <div className="flex items-center gap-2">
              <input
                type="number" min="1" max="99" step="1"
                value={margenEdit}
                onChange={e => setMargenEdit(parseFloat(e?.target?.value) || 35)}
                className="w-20 text-right font-oranienbaum font-bold rounded-lg px-2 py-1 border outline-none text-[#D6EF4E]"
                style={{ background: '#0D0D0D', borderColor: 'rgba(214,239,78,0.3)', fontSize: '1rem' }}
              />
              <span className="font-oranienbaum text-[#D6EF4E] font-bold">%</span>
            </div>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {[25, 30, 35, 40, 45, 50]?.map(m => (
              <button key={m} onClick={() => setMargenEdit(m)}
                className="px-2.5 py-1 rounded-lg font-oranienbaum text-xs transition-all"
                style={{
                  background: margenEdit === m ? `${ACCENT}20` : 'rgba(255,255,255,0.05)',
                  border: `1px solid ${margenEdit === m ? `${ACCENT}50` : 'rgba(255,255,255,0.1)'}`,
                  color: margenEdit === m ? ACCENT : 'rgba(255,255,255,0.5)',
                }}>
                {m}%
              </button>
            ))}
          </div>
        </div>

        {/* PVPs */}
        <div className="grid grid-cols-2 gap-3 mb-4">
          <div className="rounded-xl p-3" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}>
            <p className="font-oranienbaum text-white/40 text-xs mb-1">PVP Teórico</p>
            <p className="font-oranienbaum font-bold text-white/70 text-base">{fmt(pvpTeorico)}</p>
            <p className="font-oranienbaum text-white/30 text-xs mt-0.5">= {fmt(coste)} / (1 - {margenEdit}%)</p>
          </div>
          <div className="rounded-xl p-3" style={{ background: `${ACCENT}08`, border: `1px solid ${ACCENT}25` }}>
            <p className="font-oranienbaum text-[#D6EF4E]/70 text-xs mb-1">PVP Sugerido ({regla?.replace('_', ' ')})</p>
            <p className="font-oranienbaum font-bold text-[#D6EF4E] text-base">{fmt(pvpSugerido)}</p>
          </div>
          <div className="rounded-xl p-3" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}>
            <p className="font-oranienbaum text-white/40 text-xs mb-1">PVP Actual ERP</p>
            <p className="font-oranienbaum font-bold text-white text-base">{fmt(producto?.pvpActual)}</p>
            {diferencia !== null && (
              <p className={`font-oranienbaum text-xs mt-0.5 ${diferencia > 0 ? 'text-green-400' : diferencia < 0 ? 'text-red-400' : 'text-white/30'}`}>
                {diferencia > 0 ? '+' : ''}{diferencia?.toFixed(2)} €
              </p>
            )}
          </div>
          <div className="rounded-xl p-3" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}>
            <p className="font-oranienbaum text-white/40 text-xs mb-1">Margen real actual</p>
            <p className="font-oranienbaum font-bold text-white text-base">{fmtPct(producto?.margenReal)}</p>
          </div>
        </div>

        {/* Nuevo PVP editable */}
        <div className="rounded-xl p-3 mb-4" style={{ background: 'rgba(255,255,255,0.04)', border: `1px solid ${sc?.border}` }}>
          <div className="flex items-center justify-between mb-2">
            <p className="font-oranienbaum text-white/70 text-sm font-semibold">Nuevo PVP</p>
            <SemaforoBadge margenReal={margenFinal} config={config} />
          </div>
          <div className="flex items-center gap-3">
            <input
              type="number" min="0" step="0.01"
              value={nuevoPvp}
              onChange={e => setNuevoPvp(e?.target?.value)}
              placeholder={pvpSugerido ? pvpSugerido?.toFixed(2) : '0.00'}
              className="flex-1 font-oranienbaum font-bold rounded-xl px-4 py-2.5 border outline-none text-white text-lg"
              style={{ background: '#0D0D0D', borderColor: sc?.border }}
            />
            <span className="font-oranienbaum text-white/50">€</span>
          </div>
          {pvpFinal > 0 && coste > 0 && (
            <p className="font-oranienbaum text-sm mt-2" style={{ color: sc?.text }}>
              Margen real con este PVP: <strong>{fmtPct(margenFinal)}</strong>
            </p>
          )}
        </div>

        {/* Botón guardar */}
        <div className="flex gap-2">
          <button onClick={handleSave} disabled={saving || !pvpFinal}
            className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl font-oranienbaum font-bold transition-all"
            style={{ background: ACCENT, color: '#000', opacity: saving || !pvpFinal ? 0.5 : 1 }}>
            {saving
              ? <div className="w-5 h-5 rounded-full border-2 border-black/20 border-t-black animate-spin" />
              : <Icon name="Save" size={18} />}
            Guardar y sincronizar precio
          </button>
          <button onClick={loadHistorico}
            className="px-4 py-3 rounded-xl font-oranienbaum transition-all"
            style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.6)' }}>
            <Icon name="History" size={18} />
          </button>
        </div>

        {saveMsg && (
          <div className="mt-3 rounded-xl px-4 py-2.5 flex items-center gap-2"
            style={{ background: saveMsg?.ok ? 'rgba(52,211,153,0.1)' : 'rgba(239,68,68,0.1)', border: `1px solid ${saveMsg?.ok ? 'rgba(52,211,153,0.3)' : 'rgba(239,68,68,0.3)'}` }}>
            <Icon name={saveMsg?.ok ? 'CheckCircle' : 'AlertCircle'} size={16} style={{ color: saveMsg?.ok ? '#34D399' : '#EF4444' }} />
            <span className="font-oranienbaum text-sm" style={{ color: saveMsg?.ok ? '#34D399' : '#EF4444' }}>{saveMsg?.msg}</span>
          </div>
        )}
      </div>

      {/* Histórico */}
      {showHistorico && (
        <div className="rounded-2xl p-4" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.07)' }}>
          <h3 className="font-oranienbaum font-bold text-white mb-3 text-sm">Histórico de Precios</h3>
          {historico?.length === 0
            ? <p className="font-oranienbaum text-white/30 text-sm text-center py-4">Sin cambios registrados</p>
            : (
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {historico?.map((h, i) => (
                  <div key={i} className="flex items-center justify-between rounded-lg px-3 py-2"
                    style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
                    <div>
                      <span className="font-oranienbaum text-white/50 text-xs">{new Date(h.created_at)?.toLocaleDateString('es-ES')}</span>
                      <span className="font-oranienbaum text-white/30 text-xs ml-2">{h?.origen}</span>
                    </div>
                    <div className="flex items-center gap-3 text-sm font-oranienbaum">
                      <span className="text-white/40">{fmt(h?.pvp_anterior)}</span>
                      <Icon name="ArrowRight" size={12} className="text-white/20" />
                      <span className="text-white font-semibold">{fmt(h?.pvp_nuevo)}</span>
                      <span className="text-xs px-1.5 py-0.5 rounded"
                        style={{ background: h?.estado_sync_epelsa === 'SINCRONIZADO' ? 'rgba(52,211,153,0.15)' : 'rgba(251,191,36,0.15)', color: h?.estado_sync_epelsa === 'SINCRONIZADO' ? '#34D399' : '#FBBF24' }}>
                        {h?.estado_sync_epelsa}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
        </div>
      )}
    </div>
  );
};

// ─── TAB MASIVO ──────────────────────────────────────────────────────────────
const TabMasivo = ({ familias, proveedores, config }) => {
  const [filtros, setFiltros] = useState({ familia_id: '', proveedor_id: '', sinCoste: false, margenBajo: false, pvpBajoCoste: false });
  const [productos, setProductos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [seleccionados, setSeleccionados] = useState([]);
  const [margenMasivo, setMargenMasivo] = useState(35);
  const [preview, setPreview] = useState([]);
  const [confirmando, setConfirmando] = useState(false);
  const [aplicando, setAplicando] = useState(false);
  const [resultado, setResultado] = useState(null);

  const buscar = async () => {
    setLoading(true);
    setSeleccionados([]);
    setPreview([]);
    setResultado(null);
    try {
      const data = await getProductosFiltrados(filtros);
      setProductos(data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const toggleSel = (id) => setSeleccionados(prev => prev?.includes(id) ? prev?.filter(x => x !== id) : [...prev, id]);
  const toggleAll = () => setSeleccionados(seleccionados?.length === productos?.length ? [] : productos?.map(p => p?.id));

  const generarPreview = () => {
    const margenDec = margenMasivo / 100;
    const prev = seleccionados?.map(id => {
      const p = productos?.find(x => x?.id === id);
      if (!p) return null;
      const pvpNuevo = p?.costeReal > 0 ? aplicarRedondeo(calcPvpTeorico(p?.costeReal, margenDec), p?.familiaReglaRedondeo || 'TERMINAR_99') : null;
      return { ...p, pvpNuevo, margenNuevo: pvpNuevo ? calcMargenReal(pvpNuevo, p?.costeReal) : null };
    })?.filter(Boolean);
    setPreview(prev);
    setConfirmando(true);
  };

  const aplicar = async () => {
    setAplicando(true);
    try {
      const res = await aplicarCambioMasivo(seleccionados, { margenObjetivo: margenMasivo }, 'ERP');
      setResultado(res);
      setConfirmando(false);
      setSeleccionados([]);
    } catch (e) {
      setResultado({ error: e?.message });
    } finally {
      setAplicando(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Filtros */}
      <div className="rounded-2xl p-4" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}>
        <h3 className="font-oranienbaum font-bold text-white mb-3 text-sm">Filtrar productos</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
          <select value={filtros?.familia_id} onChange={e => setFiltros(f => ({ ...f, familia_id: e?.target?.value }))}
            className="font-oranienbaum rounded-xl px-3 py-2 border outline-none text-white text-sm"
            style={{ background: '#0D0D0D', borderColor: 'rgba(255,255,255,0.12)' }}>
            <option value="">Todas las familias</option>
            {familias?.map(f => <option key={f?.id} value={f?.id}>{f?.nombre}</option>)}
          </select>
          <select value={filtros?.proveedor_id} onChange={e => setFiltros(f => ({ ...f, proveedor_id: e?.target?.value }))}
            className="font-oranienbaum rounded-xl px-3 py-2 border outline-none text-white text-sm"
            style={{ background: '#0D0D0D', borderColor: 'rgba(255,255,255,0.12)' }}>
            <option value="">Todos los proveedores</option>
            {proveedores?.map(p => <option key={p?.id} value={p?.id}>{p?.nombre}</option>)}
          </select>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={filtros?.sinCoste} onChange={e => setFiltros(f => ({ ...f, sinCoste: e?.target?.checked }))} className="rounded" />
            <span className="font-oranienbaum text-white/60 text-sm">Sin coste</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={filtros?.margenBajo} onChange={e => setFiltros(f => ({ ...f, margenBajo: e?.target?.checked }))} className="rounded" />
            <span className="font-oranienbaum text-white/60 text-sm">Margen bajo</span>
          </label>
        </div>
        <button onClick={buscar} disabled={loading}
          className="flex items-center gap-2 px-4 py-2 rounded-xl font-oranienbaum font-semibold text-sm transition-all"
          style={{ background: ACCENT, color: '#000' }}>
          {loading ? <div className="w-4 h-4 rounded-full border-2 border-black/20 border-t-black animate-spin" /> : <Icon name="Search" size={15} />}
          Buscar productos
        </button>
      </div>

      {/* Tabla */}
      {productos?.length > 0 && (
        <div className="rounded-2xl overflow-hidden" style={{ border: '1px solid rgba(255,255,255,0.08)' }}>
          <div className="flex items-center justify-between px-4 py-3" style={{ background: 'rgba(255,255,255,0.04)', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
            <div className="flex items-center gap-3">
              <input type="checkbox" checked={seleccionados?.length === productos?.length && productos?.length > 0} onChange={toggleAll} className="rounded" />
              <span className="font-oranienbaum text-white/60 text-sm">{seleccionados?.length} / {productos?.length} seleccionados</span>
            </div>
            {seleccionados?.length > 0 && (
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2">
                  <span className="font-oranienbaum text-white/50 text-sm">Margen:</span>
                  <input type="number" min="1" max="99" value={margenMasivo} onChange={e => setMargenMasivo(parseFloat(e?.target?.value) || 35)}
                    className="w-16 text-right font-oranienbaum rounded-lg px-2 py-1 border outline-none text-[#D6EF4E] text-sm"
                    style={{ background: '#0D0D0D', borderColor: 'rgba(214,239,78,0.3)' }} />
                  <span className="font-oranienbaum text-[#D6EF4E] text-sm">%</span>
                </div>
                <button onClick={generarPreview}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg font-oranienbaum text-sm font-semibold"
                  style={{ background: ACCENT, color: '#000' }}>
                  <Icon name="Play" size={14} />
                  Previsualizar
                </button>
              </div>
            )}
          </div>
          <div className="overflow-x-auto max-h-72 overflow-y-auto">
            <table className="w-full text-sm font-oranienbaum">
              <thead>
                <tr style={{ background: 'rgba(255,255,255,0.03)', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                  <th className="w-8 px-3 py-2"></th>
                  {['Nombre', 'Familia', 'Coste', 'PVP Actual', 'Margen Real', 'PVP Sugerido']?.map(h => (
                    <th key={h} className="px-3 py-2 text-left text-white/40 font-medium text-xs">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {productos?.map(p => (
                  <tr key={p?.id} onClick={() => toggleSel(p?.id)} className="cursor-pointer transition-colors hover:bg-white/3"
                    style={{ borderBottom: '1px solid rgba(255,255,255,0.04)', background: seleccionados?.includes(p?.id) ? 'rgba(214,239,78,0.05)' : undefined }}>
                    <td className="px-3 py-2">
                      <input type="checkbox" checked={seleccionados?.includes(p?.id)} onChange={() => toggleSel(p?.id)} onClick={e => e?.stopPropagation()} className="rounded" />
                    </td>
                    <td className="px-3 py-2 text-white/80 max-w-[160px] truncate">{p?.nombre}</td>
                    <td className="px-3 py-2 text-white/50">{p?.familiaNombre}</td>
                    <td className="px-3 py-2 text-red-400">{fmt(p?.costeReal)}</td>
                    <td className="px-3 py-2 text-white">{fmt(p?.pvpActual)}</td>
                    <td className="px-3 py-2">
                      {p?.margenReal != null
                        ? <span style={{ color: calcSemaforo(p?.margenReal, config?.minimo, config?.objetivo, config?.premium) === 'VERDE' ? '#34D399' : '#FBBF24' }}>{fmtPct(p?.margenReal)}</span>
                        : <span className="text-white/30">—</span>}
                    </td>
                    <td className="px-3 py-2 text-[#D6EF4E]">{fmt(p?.pvpSugerido)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Preview confirmación */}
      {confirmando && preview?.length > 0 && (
        <div className="rounded-2xl p-4" style={{ background: 'rgba(251,191,36,0.06)', border: '1px solid rgba(251,191,36,0.25)' }}>
          <h3 className="font-oranienbaum font-bold text-[#FBBF24] mb-3 text-sm">Confirmar cambio masivo — {preview?.length} productos</h3>
          <div className="max-h-48 overflow-y-auto space-y-1.5 mb-4">
            {preview?.map((p, i) => (
              <div key={i} className="flex items-center justify-between rounded-lg px-3 py-2"
                style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
                <span className="font-oranienbaum text-white/70 text-sm truncate max-w-[200px]">{p?.nombre}</span>
                <div className="flex items-center gap-3 text-sm font-oranienbaum">
                  <span className="text-white/40">{fmt(p?.pvpActual)}</span>
                  <Icon name="ArrowRight" size={12} className="text-white/20" />
                  <span className="text-[#D6EF4E] font-semibold">{fmt(p?.pvpNuevo)}</span>
                  <span className="text-white/40 text-xs">{fmtPct(p?.margenNuevo)}</span>
                </div>
              </div>
            ))}
          </div>
          <div className="flex gap-2">
            <button onClick={aplicar} disabled={aplicando}
              className="flex items-center gap-2 px-4 py-2 rounded-xl font-oranienbaum font-semibold text-sm"
              style={{ background: ACCENT, color: '#000' }}>
              {aplicando ? <div className="w-4 h-4 rounded-full border-2 border-black/20 border-t-black animate-spin" /> : <Icon name="Check" size={15} />}
              Confirmar y aplicar
            </button>
            <button onClick={() => setConfirmando(false)}
              className="px-4 py-2 rounded-xl font-oranienbaum text-sm text-white/60"
              style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}>
              Cancelar
            </button>
          </div>
        </div>
      )}

      {resultado && (
        <div className="rounded-xl px-4 py-3 flex items-center gap-2"
          style={{ background: resultado?.error ? 'rgba(239,68,68,0.1)' : 'rgba(52,211,153,0.1)', border: `1px solid ${resultado?.error ? 'rgba(239,68,68,0.3)' : 'rgba(52,211,153,0.3)'}` }}>
          <Icon name={resultado?.error ? 'AlertCircle' : 'CheckCircle'} size={16} style={{ color: resultado?.error ? '#EF4444' : '#34D399' }} />
          <span className="font-oranienbaum text-sm" style={{ color: resultado?.error ? '#EF4444' : '#34D399' }}>
            {resultado?.error || `${resultado?.ok} productos actualizados correctamente`}
          </span>
        </div>
      )}
    </div>
  );
};

// ─── TAB AUDITORÍA ───────────────────────────────────────────────────────────
const TabAuditoria = () => {
  const [auditoria, setAuditoria] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getAuditoriaPrecios(50)?.then(setAuditoria)?.catch(console.error)?.finally(() => setLoading(false));
  }, []);

  return (
    <div className="rounded-2xl overflow-hidden" style={{ border: '1px solid rgba(255,255,255,0.08)' }}>
      <div className="px-4 py-3" style={{ background: 'rgba(255,255,255,0.04)', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
        <h3 className="font-oranienbaum font-bold text-white text-sm">Auditoría de cambios de precio</h3>
      </div>
      {loading
        ? <div className="flex items-center justify-center py-12 gap-3"><div className="w-5 h-5 rounded-full border-2 border-white/10 border-t-[#D6EF4E] animate-spin" /><span className="font-oranienbaum text-white/40 text-sm">Cargando...</span></div>
        : auditoria?.length === 0
          ? <p className="font-oranienbaum text-white/30 text-center py-12 text-sm">Sin cambios registrados</p>
          : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm font-oranienbaum">
                <thead>
                  <tr style={{ background: 'rgba(255,255,255,0.03)', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                    {['Producto', 'Antes', 'Después', 'Margen', 'Fecha', 'Origen', 'Epelsa']?.map(h => (
                      <th key={h} className="px-3 py-2 text-left text-white/40 font-medium text-xs">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {auditoria?.map((a, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }} className="hover:bg-white/2 transition-colors">
                      <td className="px-3 py-2 text-white/80 max-w-[160px] truncate">{a?.producto?.nombre || '—'}</td>
                      <td className="px-3 py-2 text-white/40">{fmt(a?.pvp_anterior)}</td>
                      <td className="px-3 py-2 text-white font-semibold">{fmt(a?.pvp_nuevo)}</td>
                      <td className="px-3 py-2 text-white/60">{fmtPct(a?.margen_nuevo)}</td>
                      <td className="px-3 py-2 text-white/40 text-xs">{new Date(a.created_at)?.toLocaleDateString('es-ES')}</td>
                      <td className="px-3 py-2">
                        <span className="px-1.5 py-0.5 rounded text-xs" style={{ background: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.5)' }}>{a?.origen}</span>
                      </td>
                      <td className="px-3 py-2">
                        <span className="px-1.5 py-0.5 rounded text-xs"
                          style={{
                            background: a?.estado_sync_epelsa === 'SINCRONIZADO' ? 'rgba(52,211,153,0.15)' : a?.estado_sync_epelsa === 'ERROR' ? 'rgba(239,68,68,0.15)' : 'rgba(251,191,36,0.15)',
                            color: a?.estado_sync_epelsa === 'SINCRONIZADO' ? '#34D399' : a?.estado_sync_epelsa === 'ERROR' ? '#EF4444' : '#FBBF24',
                          }}>
                          {a?.estado_sync_epelsa}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
    </div>
  );
};

// ─── PÁGINA PRINCIPAL ────────────────────────────────────────────────────────
export default function GestionPrecios() {
  const [tab, setTab] = useState('precio');
  const [query, setQuery] = useState('');
  const [searchType, setSearchType] = useState('nombre');
  const [resultados, setResultados] = useState([]);
  const [searching, setSearching] = useState(false);
  const [productoSel, setProductoSel] = useState(null);
  const [kpis, setKpis] = useState(null);
  const [familias, setFamilias] = useState([]);
  const [proveedores, setProveedores] = useState([]);
  const [config, setConfig] = useState({ minimo: 20, objetivo: 35, premium: 50 });
  const [showConfig, setShowConfig] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    getDashboardKpis()?.then(setKpis)?.catch(console.error);
    getFamilias()?.then(setFamilias)?.catch(console.error);
    getProveedores()?.then(setProveedores)?.catch(console.error);
  }, []);

  const handleSearch = useCallback(async () => {
    if (!query?.trim()) return;
    setSearching(true);
    setProductoSel(null);
    try {
      const data = await buscarProducto(query, searchType);
      setResultados(data);
    } catch (e) {
      console.error(e);
      setResultados([]);
    } finally {
      setSearching(false);
    }
  }, [query, searchType]);

  const handleKeyDown = (e) => { if (e?.key === 'Enter') handleSearch(); };

  const TABS = [
    { id: 'precio', label: 'Precio Individual', icon: 'Tag' },
    { id: 'masivo', label: 'Actualización Masiva', icon: 'Layers' },
    { id: 'auditoria', label: 'Auditoría', icon: 'Shield' },
  ];

  const KPI_ITEMS = kpis ? [
    { label: 'Margen bajo', value: kpis?.margenBajo, color: '#FBBF24', icon: 'TrendingDown' },
    { label: 'Pendientes Epelsa', value: kpis?.pendientesSync, color: '#A78BFA', icon: 'RefreshCw' },
    { label: 'PVP bajo coste', value: kpis?.pvpBajoCoste, color: '#EF4444', icon: 'AlertTriangle' },
    { label: 'Sin coste', value: kpis?.sinCoste, color: '#F97316', icon: 'HelpCircle' },
    { label: 'Sin proveedor', value: kpis?.sinProveedor, color: '#60A5FA', icon: 'Truck' },
    { label: 'Revisar precio', value: kpis?.precioRevisar, color: '#FB923C', icon: 'AlertCircle' },
  ] : [];

  return (
    <div className="min-h-screen px-4 py-5 md:px-6" style={{ background: 'linear-gradient(135deg, #0A0A0A 0%, #111111 50%, #0D0D0D 100%)' }}>
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 mb-4 font-oranienbaum text-white/30 text-xs">
        <Icon name="Home" size={12} />
        <span>/</span>
        <span className="text-[#D6EF4E]/60">Tienda General</span>
        <span>/</span>
        <span className="text-white/50">Gestión de Precios</span>
      </div>

      {/* Header */}
      <div className="flex items-center justify-between gap-4 mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{ background: 'rgba(214,239,78,0.10)', border: '1px solid rgba(214,239,78,0.25)' }}>
            <Icon name="Tag" size={20} className="text-[#D6EF4E]" />
          </div>
          <div>
            <h1 className="font-oranienbaum font-bold text-white text-xl tracking-wide">Gestión de Precios</h1>
            <p className="font-oranienbaum text-white/40 text-xs">Motor automático · PVP = Coste / (1 − Margen)</p>
          </div>
        </div>
        <button onClick={() => setShowConfig(!showConfig)}
          className="flex items-center gap-1.5 px-3 py-2 rounded-xl font-oranienbaum text-xs transition-all"
          style={{ background: showConfig ? 'rgba(214,239,78,0.1)' : 'rgba(255,255,255,0.05)', border: `1px solid ${showConfig ? 'rgba(214,239,78,0.3)' : 'rgba(255,255,255,0.1)'}`, color: showConfig ? ACCENT : 'rgba(255,255,255,0.5)' }}>
          <Icon name="Settings" size={14} />
          Config márgenes
        </button>
      </div>

      {/* Config márgenes (compacto) */}
      {showConfig && (
        <div className="rounded-2xl p-4 mb-4" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}>
          <div className="flex flex-wrap gap-4 items-center">
            {[
              { key: 'minimo', label: 'Mínimo', color: '#EF4444' },
              { key: 'objetivo', label: 'Objetivo', color: ACCENT },
              { key: 'premium', label: 'Premium', color: '#A78BFA' },
            ]?.map(m => (
              <div key={m?.key} className="flex items-center gap-2">
                <span className="w-2.5 h-2.5 rounded-full" style={{ background: m?.color }} />
                <span className="font-oranienbaum text-white/60 text-sm">{m?.label}:</span>
                <input type="number" min="0" max="99" value={config?.[m?.key]}
                  onChange={e => setConfig(c => ({ ...c, [m?.key]: parseFloat(e?.target?.value) || 0 }))}
                  className="w-16 text-right font-oranienbaum rounded-lg px-2 py-1 border outline-none text-sm"
                  style={{ background: '#0D0D0D', borderColor: `${m?.color}40`, color: m?.color }} />
                <span className="font-oranienbaum text-sm" style={{ color: m?.color }}>%</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* KPIs dashboard */}
      {kpis && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-4">
          {KPI_ITEMS?.map((k, i) => (
            <div key={i} className="rounded-xl p-3 flex items-center gap-2"
              style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
              <Icon name={k?.icon} size={16} style={{ color: k?.color }} />
              <div>
                <p className="font-oranienbaum text-white/40 text-xs">{k?.label}</p>
                <p className="font-oranienbaum font-bold text-lg leading-tight" style={{ color: k?.value > 0 ? k?.color : 'rgba(255,255,255,0.3)' }}>{k?.value}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-2 mb-4">
        {TABS?.map(t => (
          <button key={t?.id} onClick={() => setTab(t?.id)}
            className="flex items-center gap-1.5 px-3 py-2 rounded-xl font-oranienbaum text-sm transition-all"
            style={{
              background: tab === t?.id ? `${ACCENT}15` : 'rgba(255,255,255,0.04)',
              border: `1px solid ${tab === t?.id ? `${ACCENT}35` : 'rgba(255,255,255,0.08)'}`,
              color: tab === t?.id ? ACCENT : 'rgba(255,255,255,0.55)',
              fontWeight: tab === t?.id ? 600 : 400,
            }}>
            <Icon name={t?.icon} size={14} />
            {t?.label}
          </button>
        ))}
      </div>

      {/* TAB: Precio Individual */}
      {tab === 'precio' && (
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
          {/* Columna búsqueda */}
          <div className="lg:col-span-2 space-y-3">
            {/* Buscador */}
            <div className="rounded-2xl p-4" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}>
              <div className="flex flex-wrap gap-1.5 mb-3">
                {[
                  { id: 'nombre', label: 'Nombre', icon: 'Type' },
                  { id: 'ean', label: 'EAN', icon: 'Barcode' },
                  { id: 'plu', label: 'PLU', icon: 'Hash' },
                  { id: 'codigo', label: 'Código', icon: 'Tag' },
                ]?.map(t => (
                  <button key={t?.id} onClick={() => setSearchType(t?.id)}
                    className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg font-oranienbaum text-xs transition-all"
                    style={{
                      background: searchType === t?.id ? `${ACCENT}15` : 'rgba(255,255,255,0.04)',
                      border: `1px solid ${searchType === t?.id ? `${ACCENT}35` : 'rgba(255,255,255,0.08)'}`,
                      color: searchType === t?.id ? ACCENT : 'rgba(255,255,255,0.5)',
                    }}>
                    <Icon name={t?.icon} size={11} />
                    {t?.label}
                  </button>
                ))}
              </div>
              <div className="flex gap-2">
                <div className="flex items-center gap-2 flex-1 rounded-xl px-3 border" style={{ background: 'rgba(255,255,255,0.04)', borderColor: 'rgba(255,255,255,0.12)', minHeight: '42px' }}>
                  <Icon name="Search" size={16} className="text-white/30 flex-shrink-0" />
                  <input ref={inputRef} value={query} onChange={e => setQuery(e?.target?.value)} onKeyDown={handleKeyDown}
                    placeholder={searchType === 'ean' ? 'Escanear EAN...' : searchType === 'plu' ? 'PLU...' : searchType === 'codigo' ? 'Código...' : 'Nombre del producto...'}
                    className="flex-1 bg-transparent outline-none font-oranienbaum text-white placeholder-white/25 text-sm" />
                </div>
                <button onClick={handleSearch} disabled={searching}
                  className="flex items-center gap-1.5 px-4 rounded-xl font-oranienbaum font-semibold text-sm transition-all"
                  style={{ background: ACCENT, color: '#000', minHeight: '42px' }}>
                  {searching ? <div className="w-4 h-4 rounded-full border-2 border-black/20 border-t-black animate-spin" /> : <Icon name="Search" size={15} />}
                  Buscar
                </button>
              </div>
            </div>

            {/* Resultados */}
            {resultados?.length > 0 && (
              <div className="rounded-2xl overflow-hidden" style={{ border: '1px solid rgba(255,255,255,0.08)' }}>
                <div className="px-3 py-2" style={{ background: 'rgba(255,255,255,0.04)', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                  <span className="font-oranienbaum text-white/40 text-xs">{resultados?.length} resultado{resultados?.length !== 1 ? 's' : ''}</span>
                </div>
                <div className="max-h-80 overflow-y-auto">
                  {resultados?.map(p => {
                    const sel = productoSel?.id === p?.id;
                    return (
                      <button key={p?.id} onClick={() => setProductoSel(p)}
                        className="w-full flex items-start gap-3 px-3 py-3 text-left transition-all hover:bg-white/3"
                        style={{ borderBottom: '1px solid rgba(255,255,255,0.04)', background: sel ? 'rgba(214,239,78,0.06)' : undefined }}>
                        <div className="flex-1 min-w-0">
                          <p className="font-oranienbaum font-semibold text-sm truncate" style={{ color: sel ? ACCENT : 'rgba(255,255,255,0.85)' }}>{p?.nombre}</p>
                          <p className="font-oranienbaum text-white/35 text-xs mt-0.5">{p?.familiaNombre} · {p?.proveedorNombre}</p>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <p className="font-oranienbaum font-bold text-sm text-white">{fmt(p?.pvpActual)}</p>
                          <p className="font-oranienbaum text-xs" style={{ color: p?.margenReal != null ? '#34D399' : 'rgba(255,255,255,0.3)' }}>{fmtPct(p?.margenReal)}</p>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {!searching && resultados?.length === 0 && query && (
              <div className="rounded-2xl p-6 flex flex-col items-center gap-3" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.07)' }}>
                <Icon name="SearchX" size={28} className="text-white/20" />
                <p className="font-oranienbaum text-white/40 text-sm text-center">No se encontraron productos para "{query}"</p>
              </div>
            )}

            {!query && (
              <div className="rounded-2xl p-6 flex flex-col items-center gap-3" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.07)' }}>
                <Icon name="Search" size={28} className="text-white/15" />
                <p className="font-oranienbaum text-white/30 text-sm text-center">Busca un producto por nombre, EAN, PLU o código</p>
              </div>
            )}
          </div>

          {/* Columna ficha */}
          <div className="lg:col-span-3">
            {productoSel
              ? <FichaProducto producto={productoSel} config={config} onSaved={() => getDashboardKpis()?.then(setKpis)?.catch(console.error)} />
              : (
                <div className="rounded-2xl p-10 flex flex-col items-center gap-4 h-full" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.07)' }}>
                  <div className="w-16 h-16 rounded-2xl flex items-center justify-center" style={{ background: 'rgba(214,239,78,0.06)', border: '1px solid rgba(214,239,78,0.15)' }}>
                    <Icon name="Tag" size={28} className="text-[#D6EF4E]/40" />
                  </div>
                  <div className="text-center">
                    <p className="font-oranienbaum font-bold text-white/50 text-base">Selecciona un producto</p>
                    <p className="font-oranienbaum text-white/25 text-sm mt-1">Busca y selecciona un producto para ver y editar su precio</p>
                  </div>
                  <div className="mt-2 rounded-xl px-4 py-3 text-center" style={{ background: 'rgba(251,191,36,0.06)', border: '1px solid rgba(251,191,36,0.15)' }}>
                    <p className="font-oranienbaum text-[#FBBF24]/70 text-xs">⚠️ Sync Epelsa: PENDIENTE DE INTEGRACIÓN</p>
                    <p className="font-oranienbaum text-white/25 text-xs mt-0.5">Los precios se guardan en ERP y se encolan para Epelsa</p>
                  </div>
                </div>
              )}
          </div>
        </div>
      )}

      {/* TAB: Masivo */}
      {tab === 'masivo' && <TabMasivo familias={familias} proveedores={proveedores} config={config} />}

      {/* TAB: Auditoría */}
      {tab === 'auditoria' && <TabAuditoria />}
    </div>
  );
}
